import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { TokenAddressKey, TokenResult } from '@/utils/0x/token-registry.types';
import { normalizeTokenAddress, tokenKey } from '@/utils/normalizeAddress';

import { tokenEntityQueryOptions } from './tokenEntityCache';

interface TokenMap {
  [chainId: number]: {
    [address: string]: TokenResult;
  };
}

const combineTokenResults = (
  queries: readonly { data: TokenResult | null | undefined; isLoading: boolean }[],
) => {
  const tokenMap: TokenMap = {};
  let isLoading = false;
  for (const query of queries) {
    isLoading ||= query.isLoading;
    const token = query.data;
    if (!token) continue;
    const chainMap = tokenMap[token.chainId] ?? (tokenMap[token.chainId] = {});
    chainMap[normalizeTokenAddress(token.address, token.chainId)] = token;
  }
  return { tokenMap, isLoading };
};

/** Resolve only the supplied address/chain pairs, sharing the token entity cache. */
export function useTokenResultsByKeys(keys: readonly TokenAddressKey[] | undefined) {
  const queries = useMemo(() => {
    const pairs = new Map<string, TokenAddressKey>();
    for (const { address, chainId } of keys ?? []) {
      if (!address || !chainId) continue;
      pairs.set(tokenKey(address, chainId), {
        address: normalizeTokenAddress(address, chainId),
        chainId,
      });
    }
    return Array.from(pairs.values(), ({ address, chainId }) => ({
      ...tokenEntityQueryOptions(address, chainId),
      // Avoid useQueries tracking each property across every token observer.
      notifyOnChangeProps: ['data' as const, 'isLoading' as const],
    }));
  }, [keys]);

  return useQueries({ queries, combine: combineTokenResults });
}

/**
 * Get token results for many tokens by contract address and chain ID. Each token
 * is its own entity query; the concurrent fetches are coalesced into batched
 * requests by the shared token loader.
 */
export function useTokenResults(
  tokenAddresses: string[] | undefined,
  chainId: number | undefined,
): {
  isLoading: boolean;
  tokenMap: TokenMap | undefined;
} {
  const addresses = useMemo(() => {
    if (!tokenAddresses || !chainId) return [];
    return [...new Set(tokenAddresses.map((address) => normalizeTokenAddress(address, chainId)))];
  }, [tokenAddresses, chainId]);

  return useQueries({
    queries: addresses.map((address) => tokenEntityQueryOptions(address, chainId)),
    combine: (queries) => {
      const map: TokenMap = {};
      queries.forEach((query) => {
        if (!query.data) return;
        const chainMap = map[query.data.chainId] ?? (map[query.data.chainId] = {});
        chainMap[normalizeTokenAddress(query.data.address, query.data.chainId)] = query.data;
      });

      return {
        tokenMap: map,
        isLoading: queries.some((query) => query.isLoading),
      };
    },
  });
}

/**
 * Get token results for many tokens by contract address across multiple chain IDs.
 */
export function useTokenResultsMultiChain(
  tokenAddresses: string[] | undefined,
  chainIds: number[] | undefined,
): {
  isLoading: boolean;
  tokenMap: TokenMap | undefined;
} {
  const pairs = useMemo(() => {
    if (!tokenAddresses || !chainIds) return [] as { address: string; chainId: number }[];
    return Array.from(
      new Map(
        chainIds.flatMap((chainId) =>
          tokenAddresses.map(
            (address) => [tokenKey(address, chainId), { address, chainId }] as const,
          ),
        ),
      ).values(),
    );
  }, [tokenAddresses, chainIds]);

  return useQueries({
    queries: pairs.map(({ address, chainId }) => tokenEntityQueryOptions(address, chainId)),
    combine: (queries) => {
      const map: TokenMap = {};
      queries.forEach((query) => {
        if (!query.data) return;
        const chainMap = map[query.data.chainId] ?? (map[query.data.chainId] = {});
        chainMap[normalizeTokenAddress(query.data.address, query.data.chainId)] = query.data;
      });

      return {
        tokenMap: map,
        isLoading: queries.some((query) => query.isLoading),
      };
    },
  });
}
